import { EnvCard2RealCard, parseDouzeroCardString } from './cardEnv';
import { getLegalCardPlayActions } from './legalActions';
import { buildModelInputs, pickTopActions } from './deepFeatures';
import { ensureDouzeroModels, runInference } from './modelManager';

function envActionToString(action) {
    return action.map((a) => EnvCard2RealCard[a]).join('');
}

function parseCardPlaySeq(seqStr) {
    if (!seqStr) return [];
    return seqStr.split(',').map((part) => parseDouzeroCardString(part));
}

function buildInfoSet(body) {
    const playerPosition = parseInt(body.player_position, 10);
    const playerHandCards = parseDouzeroCardString(body.player_hand_cards);
    const numCardsLeft = [
        parseInt(body.num_cards_left_landlord, 10),
        parseInt(body.num_cards_left_landlord_down, 10),
        parseInt(body.num_cards_left_landlord_up, 10),
    ];
    const threeLandlordCards = parseDouzeroCardString(body.three_landlord_cards);
    const cardPlayActionSeq = parseCardPlaySeq(body.card_play_action_seq);
    const useOracle = body.use_oracle === '1' || body.use_oracle === true;
    const otherHandCards = useOracle ? parseDouzeroCardString(body.other_hand_cards) : [];

    const lastMoves = [
        parseDouzeroCardString(body.last_move_landlord),
        parseDouzeroCardString(body.last_move_landlord_down),
        parseDouzeroCardString(body.last_move_landlord_up),
    ];
    const playedCards = [
        parseDouzeroCardString(body.played_cards_landlord),
        parseDouzeroCardString(body.played_cards_landlord_down),
        parseDouzeroCardString(body.played_cards_landlord_up),
    ];
    const bombNum = parseInt(body.bomb_num, 10);

    let rivalMove = [];
    if (cardPlayActionSeq.length !== 0) {
        const last = cardPlayActionSeq[cardPlayActionSeq.length - 1];
        if (last.length === 0) {
            rivalMove = cardPlayActionSeq[cardPlayActionSeq.length - 2] || [];
        } else {
            rivalMove = last;
        }
    }

    const legalActions = getLegalCardPlayActions(playerHandCards, rivalMove);

    return {
        player_position: playerPosition,
        player_hand_cards: playerHandCards,
        num_cards_left: numCardsLeft,
        three_landlord_cards: threeLandlordCards,
        card_play_action_seq: cardPlayActionSeq,
        other_hand_cards: otherHandCards,
        last_moves: lastMoves,
        played_cards: playedCards,
        bomb_num: bombNum,
        rival_move: rivalMove,
        legal_actions: legalActions,
    };
}

export async function douzeroPredict(body) {
    try {
        await ensureDouzeroModels();

        const playerPosition = parseInt(body.player_position, 10);
        if (![0, 1, 2].includes(playerPosition)) {
            return { status: 1, message: 'player_position must be 0, 1, or 2' };
        }

        const infoset = buildInfoSet(body);
        const { legal_actions: legalActions } = infoset;

        if (legalActions.length === 0) {
            return { status: 0, message: 'success', result: {}, win_rates: {} };
        }

        const { xBatch, zBatch, numLegal } = buildModelInputs(infoset);
        const yPred = await runInference(playerPosition, xBatch, zBatch, numLegal);
        const { actions, confidences } = pickTopActions(yPred, legalActions, 3);

        const result = {};
        const winRates = {};
        actions.forEach((action, i) => {
            const actionStr = envActionToString(action);
            let winRate = Math.max(confidences[i], -1);
            winRate = Math.min(winRate, 1);
            winRates[actionStr] = String(Math.round(((winRate + 1) / 2) * 10000) / 10000);
            result[actionStr] = String(Math.round(confidences[i] * 1000000) / 1000000);
        });

        return { status: 0, message: 'success', result, win_rates: winRates };
    } catch (err) {
        console.error('douzeroPredict', err);
        return { status: -1, message: err.message || 'unknown error' };
    }
}

export async function douzeroLegal(body) {
    try {
        const playerHandCards = parseDouzeroCardString(body.player_hand_cards);
        const rivalMove = parseDouzeroCardString(body.rival_move);
        const legalActions = getLegalCardPlayActions(playerHandCards, rivalMove);
        const legalAction = legalActions.map((a) => envActionToString(a)).join(',');
        return { status: 0, message: 'success', legal_action: legalAction };
    } catch (err) {
        console.error('douzeroLegal', err);
        return { status: -1, message: err.message || 'unknown error' };
    }
}

export { ensureDouzeroModels, areModelsLoaded, getModelLoadError } from './modelManager';
